import { useState, useRef, useId, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLabelOptions } from '@/hooks/useLabelOptions';
import { useDebounce } from '@/hooks/useDebounce';
import { normalizeLabelKey } from '@/utils/labels';
import type { LabelOption } from '@/types/labels';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';

/**
 * LabelInput Component
 *
 * Tag input with autocomplete suggestions from backend
 * Features:
 * - Add labels by typing and pressing Enter or comma
 * - Remove labels by clicking X
 * - Autocomplete suggestions from API
 * - Popular labels displayed
 */

interface LabelInputProps {
  labels: string[];
  onChange: (labels: string[]) => void;
  disabled?: boolean;
  maxLabels?: number;
}

export const LabelInput = ({
  labels,
  onChange,
  disabled = false,
  maxLabels = 10,
}: LabelInputProps) => {
  const { t } = useTranslation('common');
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const isClickingRef = useRef(false);

  const trimmedInput = inputValue.trim();
  const debouncedQuery = useDebounce(trimmedInput, 300);
  const queryEnabled = debouncedQuery.length >= 2;
  const { data: fetched } = useLabelOptions({ q: debouncedQuery, limit: 10, enabled: queryEnabled, keepPrevious: true });
  const suggestions: LabelOption[] = queryEnabled && trimmedInput.length >= 2 ? fetched ?? [] : [];
  // Same condition that renders the dropdown below; exposed as aria-expanded so a surrounding Modal keeps Esc for it.
  const suggestionsOpen = showSuggestions && inputValue.trim().length >= 2;
  const listId = useId();
  const createOptionId = `${listId}-create`;
  const inputKey = normalizeLabelKey(inputValue);
  const showCreateOption = inputValue.trim() !== '' && !suggestions.some((s) => normalizeLabelKey(s.name) === inputKey);
  // Keyboard highlight: -1 = the "create" row (when shown), >= 0 = a suggestion.
  const activeOptionId = !suggestionsOpen
    ? undefined
    : selectedIndex >= 0 && suggestions[selectedIndex]
      ? `${listId}-opt-${selectedIndex}`
      : selectedIndex === -1 && showCreateOption
        ? createOptionId
        : undefined;

  /**
   * Add label
   */
  const addLabel = (label: string) => {
    const trimmed = label.trim();

    if (!trimmed) return;

    // Check if already exists (same fold as backend)
    const key = normalizeLabelKey(trimmed);
    if (labels.some((l) => normalizeLabelKey(l) === key)) {
      setInputValue('');
      setShowSuggestions(false);
      return;
    }

    // Check max labels
    if (labels.length >= maxLabels) {
      alert(t('max_labels_allowed', { max: maxLabels }));
      return;
    }

    onChange([...labels, trimmed]);
    setInputValue('');
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  /**
   * Remove label
   */
  const removeLabel = (index: number) => {
    onChange(labels.filter((_, i) => i !== index));
  };

  /**
   * Check if input value is an exact match with existing suggestions
   */
  const hasExactMatch = () => {
    const key = normalizeLabelKey(inputValue);
    return suggestions.some((s) => normalizeLabelKey(s.name) === key);
  };

  /**
   * Handle key down
   */
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      // Priority 1: If a suggestion is selected, use it
      if (selectedIndex >= 0 && suggestions[selectedIndex]) {
        addLabel(suggestions[selectedIndex].name);
      }
      // Priority 2: If user typed something, add it as new label
      else if (inputValue.trim()) {
        addLabel(inputValue);
      }
      // Priority 3: Nothing to add (empty input)
      return;
    } else if (e.key === ',' && inputValue.trim()) {
      e.preventDefault();
      addLabel(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && labels.length > 0) {
      // Remove last label if input is empty
      removeLabel(labels.length - 1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      // If no selection and "Create new" option exists, select it first
      if (selectedIndex === -1 && inputValue.trim() && !hasExactMatch()) {
        setSelectedIndex(0);
      } else {
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => {
        // If at first suggestion and "Create new" option exists, go to it
        if (prev === 0 && inputValue.trim() && !hasExactMatch()) {
          return -1;
        }
        return prev > 0 ? prev - 1 : -1;
      });
    } else if (e.key === 'Escape') {
      // Consume the key only when it closes the suggestion list (not the surrounding layer).
      if (showSuggestions) e.preventDefault();
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }
  };

  /**
   * Handle suggestion click
   */
  const handleSuggestionClick = (label: LabelOption) => {
    addLabel(label.name);
    inputRef.current?.focus();
  };

  /**
   * Handle input blur
   */
  const handleBlur = () => {
    // Delay to allow click on suggestion
    setTimeout(() => {
      if (!isClickingRef.current) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
      isClickingRef.current = false;
    }, 150);
  };

  /**
   * Handle input focus
   */
  const handleFocus = () => {
    if (trimmedInput.length >= 2) {
      setShowSuggestions(true);
    }
  };

  return (
    <div className="relative">
      {/* Input Container */}
      <div
        className={cn(
          'flex flex-wrap gap-1.5 lg:gap-2 p-2 lg:p-3 border rounded-lg bg-white transition-colors',
          disabled
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
            : 'border-gray-300 focus-within:border-gray-400'
        )}
      >
        {/* Label Tags */}
        {labels.map((label, index) => (
          <span
            key={`${label}-${index}`}
            className="inline-flex items-center gap-1 px-1.5 lg:px-2 py-0.5 lg:py-1 bg-soft-mint text-dark-teal rounded-md text-xs lg:text-sm font-medium"
          >
            <Icon name={ICONS.TAG} size="xs" />
            {label}
            <button
              type="button"
              onClick={() => removeLabel(index)}
              disabled={disabled}
              className={cn(
                'ml-1 rounded-full hover:bg-soft-mint/70 transition-colors',
                // 44px hit area below sm; negative margins keep the chip layout unchanged
                'max-sm:p-[17px] max-sm:-my-[17px] max-sm:-mr-[17px] max-sm:-ml-[13px]',
                disabled && 'cursor-not-allowed opacity-50'
              )}
              aria-label={t('remove_label', { label })}
            >
              <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="lg:w-3 lg:h-3"><line x1="12" y1="4" x2="4" y2="12" /><line x1="4" y1="4" x2="12" y2="12" /></svg>
            </button>
          </span>
        ))}

        {/* Input Field */}
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={suggestionsOpen}
          aria-controls={suggestionsOpen ? listId : undefined}
          aria-activedescendant={activeOptionId}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setShowSuggestions(true);
            setSelectedIndex(-1);
          }}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={handleFocus}
          disabled={disabled || labels.length >= maxLabels}
          placeholder={
            labels.length === 0
              ? t('type_labels_hint')
              : labels.length >= maxLabels
              ? t('max_labels', { max: maxLabels })
              : ''
          }
          className="flex-1 min-w-[80px] lg:min-w-[120px] outline-none bg-transparent disabled:cursor-not-allowed text-xs lg:text-sm"
        />
      </div>

      {/* Suggestions Dropdown — right after input, before helper text */}
      {suggestionsOpen && (
        <div
          ref={suggestionsRef}
          id={listId}
          role="listbox"
          className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
        >
          {/* Create New Option - show when input doesn't match any existing */}
          {showCreateOption && (
            <button
              type="button"
              id={createOptionId}
              role="option"
              aria-selected={selectedIndex === -1}
              tabIndex={-1}
              onMouseDown={(e) => {
                e.preventDefault();
                isClickingRef.current = true;
                addLabel(inputValue);
                inputRef.current?.focus();
              }}
              className={cn(
                'w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors border-b border-gray-200',
                selectedIndex === -1 && 'bg-soft-mint'
              )}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-900">
                  {t('create_label', { value: inputValue.trim() })}
                </span>
                <span className="text-xs text-dark-teal font-medium">
                  {t('press_enter')}
                </span>
              </div>
            </button>
          )}

          {/* Existing Suggestions */}
          {suggestions.length > 0 && (
            <>
              {inputValue.trim() && !hasExactMatch() && (
                <div className="px-3 py-1 text-xs text-gray-500 font-medium uppercase tracking-wide">
                  {t('or_select_existing')}
                </div>
              )}
              {suggestions.map((suggestion, index) => (
                <button
                  key={suggestion.name}
                  id={`${listId}-opt-${index}`}
                  role="option"
                  aria-selected={selectedIndex === index}
                  tabIndex={-1}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    isClickingRef.current = true;
                    handleSuggestionClick(suggestion);
                  }}
                  className={cn(
                    'w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors',
                    selectedIndex === index && 'bg-soft-mint'
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-900">
                      {suggestion.name}
                    </span>
                    <span className="text-xs text-gray-500">
                      {t('used_x_times', { count: suggestion.count })}
                    </span>
                  </div>
                </button>
              ))}
            </>
          )}

          {/* No results state */}
          {suggestions.length === 0 && hasExactMatch() && (
            <div className="px-3 py-2 text-sm text-gray-500 italic">
              {t('no_matching_labels')}
            </div>
          )}
        </div>
      )}

      {/* Helper Text */}
      <p className="mt-2 text-xs text-bs-gray">
        {inputValue.trim() &&
        inputValue.trim().length >= 2 &&
        !hasExactMatch() ? (
          <span className="text-dark-teal font-medium">
            {t('press_enter_create', { value: inputValue.trim() })}
          </span>
        ) : (
          <>
            {t('labels_used', { used: labels.length, max: maxLabels })}
          </>
        )}
      </p>
    </div>
  );
};
